import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  AddOutlined,
  BlockOutlined,
  CheckCircleOutlineOutlined,
  DeleteOutlineOutlined,
  EditOutlined,
  ExpandMoreOutlined,
  HourglassEmptyOutlined,
  PlaylistAddCheckCircleOutlined,
  RemoveOutlined,
  RestartAltOutlined,
  SearchOutlined,
  TrendingFlatOutlined,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import ProductFormatSummary from "./components/ProductFormatSummary";
import StockCountCrossDetailSummary from "./components/StockCountCrossDetailSummary";
import {
  formatStockCountNumber,
  formatStockCountReadableQty,
  getStockCountPresentationsFromItem,
} from "./stockCountQtyFormat";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const INTEGER_QTY_MESSAGE = "La cantidad debe ser un numero entero. Utilice el formato correspondiente.";
const isPositiveIntegerInput = (value) => /^[1-9]\d*$/.test(String(value || "").trim());
const hasDecimalSeparator = (value) => /[.,]/.test(String(value || ""));

const formatDisplayQty = (value, decimals = 2) => formatStockCountNumber(value, decimals);

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const getFormatName = (item) => item?.product_presentation?.product_format?.name || "unidad";

const getQtyInBase = (item) => {
  const qtyInBase = toNumber(item?.product_presentation?.qty_in_base);
  return qtyInBase > 0 ? qtyInBase : 1;
};

const formatSystemQty = (item) =>
  formatStockCountReadableQty(item?.system_qty_base, getStockCountPresentationsFromItem(item));

const formatCountedQty = (item) =>
  formatStockCountReadableQty(item?.counted_qty_base, getStockCountPresentationsFromItem(item));

const formatDiffQty = (item) => {
  const diff = toNumber(item?.diff_qty_base);
  if (Math.abs(diff) <= 0.000001) {
    return formatStockCountReadableQty(0, getStockCountPresentationsFromItem(item));
  }
  return formatStockCountReadableQty(diff, getStockCountPresentationsFromItem(item), { showPlus: true });
};

const hasPendingCashierSales = (item) => Boolean(item?.has_pending_cashier_sales);

const formatPendingCashierQty = (item) =>
  formatStockCountReadableQty(item?.pending_cashier_sales_qty_base, getStockCountPresentationsFromItem(item));

const formatEstimatedAfterPendingCashierQty = (item) =>
  formatStockCountReadableQty(
    item?.estimated_available_after_pending_cashier_qty_base,
    getStockCountPresentationsFromItem(item)
  );

const buildMetrics = (items) => {
  const total = items.length;
  const counted = items.filter((item) => item?.is_counted).length;
  const pending = total - counted;
  const withDiff = items.filter(
    (item) => item?.is_counted && Math.abs(toNumber(item?.diff_qty_base)) > 0.0001
  ).length;
  const withoutDiff = items.filter(
    (item) => item?.is_counted && Math.abs(toNumber(item?.diff_qty_base)) <= 0.0001
  ).length;
  return { total, counted, pending, withDiff, withoutDiff };
};

function MetricCard({ label, value, icon, color = "primary.main" }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.2, "&:last-child": { pb: 1.2 } }}>
        <Stack direction="row" spacing={1.1} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: "action.hover",
              color,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function StockCountPage({
  title = "Conteo fisico",
  subtitle = "Captura progresiva del inventario por presentacion",
  countId,
  countStatus,
  countAppliedAt = null,
  items = [],
  loading = false,
  error = "",
  filterMode,
  setFilterMode,
  searchQuery,
  setSearchQuery,
  filteredItems = [],
  addToItem,
  addEntry,
  updateEntry,
  voidEntry,
  resetItem,
  deleteProduct,
  submitCount,
  isReadOnly = false,
  headerAlert = null,
  settingsSnapshot = {},
  groups = [],
  crosses = [],
  activeGroupId = null,
  onActiveGroupChange,
  onJoinGroup,
  onAddGroupMember,
  isReviewCorrectionMode = false,
  reviewReturnPath = "",
  onSubmitted,
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusItemId = useMemo(() => {
    const raw = Number(searchParams.get("item_id"));
    return Number.isInteger(raw) && raw > 0 ? raw : null;
  }, [searchParams]);

  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [entryDrafts, setEntryDrafts] = useState(() => new Map());
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [voidDialog, setVoidDialog] = useState({ open: false, entry: null, reason: "" });
  const [deleteProductDialog, setDeleteProductDialog] = useState({ open: false, group: null });
  const [deletingProduct, setDeletingProduct] = useState(false);
  const [crossIntegrityDialog, setCrossIntegrityDialog] = useState({ open: false, events: [] });
  const [joinGroupDialogOpen, setJoinGroupDialogOpen] = useState(false);
  const [joinGroupRole, setJoinGroupRole] = useState("COUNTER");
  const [memberNameDraft, setMemberNameDraft] = useState("");
  const [memberRoleDraft, setMemberRoleDraft] = useState("COUNTER");
  const [groupSubmitting, setGroupSubmitting] = useState(false);
  const [resetConfirmItemId, setResetConfirmItemId] = useState(null);
  const [savingItem, setSavingItem] = useState(false);
  const [resettingItemId, setResettingItemId] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const searchInputRef = useRef(null);
  const countInputRef = useRef(null);
  const editingEntryRef = useRef(null);

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  const handleJoinGroup = async (group) => {
    if (!group?.id || typeof onJoinGroup !== "function") return;
    setGroupSubmitting(true);
    try {
      const updated = await onJoinGroup(group.id, { role: joinGroupRole });
      if ((updated?.id || group.id) && typeof onActiveGroupChange === "function") {
        onActiveGroupChange(updated?.id || group.id);
      }
      setJoinGroupDialogOpen(false);
      setJoinGroupRole("COUNTER");
      showSnack("Te uniste al grupo.", "success");
    } catch (requestError) {
      showSnack(requestError?.response?.data?.detail || "No se pudo unir al grupo.", "error");
    } finally {
      setGroupSubmitting(false);
    }
  };

  const handleAddMember = async () => {
    const memberName = String(memberNameDraft || "").trim();
    if (!activeGroup?.id || !memberName || typeof onAddGroupMember !== "function") return;
    setGroupSubmitting(true);
    try {
      await onAddGroupMember(activeGroup.id, { member_name: memberName, role: memberRoleDraft });
      setMemberNameDraft("");
      showSnack("Miembro agregado.", "success");
    } catch (requestError) {
      showSnack(requestError?.response?.data?.detail || "No se pudo agregar el miembro.", "error");
    } finally {
      setGroupSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isReadOnly) {
      searchInputRef.current?.focus();
    }
  }, [isReadOnly]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "F2" && !isReadOnly) {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isReadOnly]);

  useEffect(() => {
    if (selectedProductId && selectedItemId && !isReadOnly) {
      countInputRef.current?.focus();
      countInputRef.current?.select?.();
    }
  }, [isReadOnly, selectedItemId, selectedProductId]);

  const metrics = useMemo(() => buildMetrics(toArray(items)), [items]);
  const progressValue = metrics.total > 0 ? (metrics.counted / metrics.total) * 100 : 0;
  const pendingCashierItems = useMemo(
    () => toArray(items).filter((item) => hasPendingCashierSales(item)),
    [items]
  );
  const movedDuringCountItems = useMemo(
    () => toArray(items).filter((item) => Boolean(item?.has_stock_movements_after_count_started)),
    [items]
  );
  const numberedFilteredItems = useMemo(
    () => toArray(filteredItems).map((item, index) => ({ ...item, __rowNumber: index + 1 })),
    [filteredItems]
  );

  const searchBaseItems = useMemo(
    () => (String(searchQuery || "").trim() ? toArray(filteredItems) : toArray(items)),
    [filteredItems, items, searchQuery]
  );

  const productGroups = useMemo(() => {
    const grouped = new Map();
    searchBaseItems.forEach((item) => {
      const product = item?.product_presentation?.product;
      const productId = String(product?.id || "");
      if (!productId) return;

      if (!grouped.has(productId)) {
        grouped.set(productId, {
          productId,
          code: product?.code || "",
          description: product?.description || "Producto",
          product,
          items: [],
        });
      }
      grouped.get(productId).items.push(item);
    });

    return Array.from(grouped.values())
      .map((group) => ({
        ...group,
        items: [...group.items].sort(
          (left, right) =>
            getQtyInBase(left) - getQtyInBase(right) ||
            String(getFormatName(left)).localeCompare(String(getFormatName(right)))
        ),
        hasPendingCashierSales: group.items.some((item) => hasPendingCashierSales(item)),
        hasMovementDuringCount: group.items.some((item) => Boolean(item?.has_stock_movements_after_count_started)),
      }))
      .sort((left, right) => left.description.localeCompare(right.description));
  }, [searchBaseItems]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [productGroups.length, searchQuery]);

  const selectedGroup = useMemo(
    () => productGroups.find((group) => String(group.productId) === String(selectedProductId)) || null,
    [productGroups, selectedProductId]
  );

  const activeCrosses = useMemo(
    () =>
      toArray(crosses).filter(
        (cross) =>
          String(cross?.status || "").toUpperCase() === "APPLIED" &&
          !["VOIDED", "AUTO_VOIDED"].includes(String(cross?.cross_status || "ACTIVE").toUpperCase())
      ).sort((left, right) => Number(left?.id || 0) - Number(right?.id || 0)),
    [crosses]
  );
  const crossRowsByItem = useMemo(() => {
    const grouped = new Map();
    const pushRow = (itemId, row) => {
      const key = String(itemId || "");
      if (!key) return;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(row);
    };
    activeCrosses.forEach((cross) => {
      pushRow(cross.source_item_id, {
        direction: "OUT",
        label: "Entrega",
        color: "info",
        relatedProduct: cross?.target_product?.description || "Destino",
        qtyBase: cross?.qty_base,
        cross,
      });
      pushRow(cross.target_item_id, {
        direction: "IN",
        label: "Recibe",
        color: "success",
        relatedProduct: cross?.source_product?.description || "Origen",
        qtyBase: cross?.qty_base,
        cross,
      });
    });
    return grouped;
  }, [activeCrosses]);
  const formatCrossRowQty = useCallback(
    (row, item) => formatStockCountReadableQty(row?.qtyBase, getStockCountPresentationsFromItem(item)),
    []
  );

  const productGroupHasRegisteredData = useCallback(
    (group) => {
      const itemIds = new Set(toArray(group?.items).map((item) => String(item?.id)));
      if (itemIds.size === 0) return false;
      const hasItemData = toArray(group?.items).some(
        (item) =>
          item?.is_counted ||
          Math.abs(toNumber(item?.diff_qty_base)) > 0.0001 ||
          toArray(item?.entries).length > 0
      );
      if (hasItemData) return true;
      return activeCrosses.some(
        (cross) => itemIds.has(String(cross?.source_item_id)) || itemIds.has(String(cross?.target_item_id))
      );
    },
    [activeCrosses]
  );

  const selectedItem = useMemo(() => {
    if (!selectedGroup) return null;
    return (
      selectedGroup.items.find((item) => String(item.id) === String(selectedItemId)) ||
      selectedGroup.items[0] ||
      null
    );
  }, [selectedGroup, selectedItemId]);

  const selectedEntryDraft = useMemo(() => {
    if (!selectedItem) return {};
    return entryDrafts.get(selectedItem.id) || {};
  }, [entryDrafts, selectedItem]);

  useEffect(() => {
    const hadEditingEntry = Boolean(editingEntryRef.current);
    if (!editingEntryId) {
      if (hadEditingEntry) {
        setNotesExpanded(false);
      }
      editingEntryRef.current = editingEntryId;
      return;
    }
    if (selectedEntryDraft.note || selectedEntryDraft.correction_reason) {
      setNotesExpanded(true);
    }
    editingEntryRef.current = editingEntryId;
  }, [editingEntryId, selectedEntryDraft.correction_reason, selectedEntryDraft.note]);

  const selectedInputValue = selectedEntryDraft.qty_presentation ?? "";
  const selectedAvailablePresentations = useMemo(() => {
    if (!selectedItem) return [];
    const rows = toArray(selectedItem?.available_presentations);
    if (rows.length > 0) return rows;
    return [
      {
        id: selectedItem?.product_presentation?.id,
        format_name: selectedItem?.product_presentation?.product_format?.name || "unidad",
        qty_in_base: selectedItem?.product_presentation?.qty_in_base || "1",
      },
    ].filter((row) => row.id);
  }, [selectedItem]);
  const selectedEntryPresentationId =
    selectedEntryDraft.product_presentation_id ||
    selectedItem?.product_presentation?.id ||
    selectedAvailablePresentations[0]?.id ||
    "";
  const selectedEntryPresentation = useMemo(
    () =>
      selectedAvailablePresentations.find(
        (presentation) => String(presentation?.id) === String(selectedEntryPresentationId)
      ) ||
      selectedAvailablePresentations[0] ||
      null,
    [selectedAvailablePresentations, selectedEntryPresentationId]
  );
  const selectedEntries = useMemo(() => toArray(selectedItem?.entries), [selectedItem]);
  const groupsEnabled = Boolean(settingsSnapshot?.selective_enable_count_groups);
  const stockCountGroups = useMemo(() => toArray(groups), [groups]);
  const activeGroup = useMemo(
    () =>
      stockCountGroups.find(
        (group) =>
          String(group?.id) === String(activeGroupId) && Boolean(group?.current_user_is_member)
      ) || null,
    [activeGroupId, stockCountGroups]
  );
  const currentUserGroup = useMemo(
    () => stockCountGroups.find((group) => Boolean(group?.current_user_is_member)) || null,
    [stockCountGroups]
  );
  useEffect(() => {
    if (currentUserGroup?.id && String(currentUserGroup.id) !== String(activeGroupId || "")) {
      onActiveGroupChange?.(currentUserGroup.id);
    }
  }, [activeGroupId, currentUserGroup?.id, onActiveGroupChange]);
  const mustSelectGroup = Boolean(groupsEnabled && !isReadOnly && !isReviewCorrectionMode && !activeGroup?.id);

  const selectedSystemHumanized = useMemo(() => {
    if (!selectedItem) return "-";
    return formatStockCountReadableQty(
      selectedItem?.system_qty_base,
      getStockCountPresentationsFromItem(selectedItem)
    );
  }, [selectedItem]);
  const selectedSystemInFormat = useMemo(() => {
    if (!selectedItem) return "-";
    return formatSystemQty(selectedItem);
  }, [selectedItem]);

  const liveDiffMeta = useMemo(() => {
    if (!selectedItem) {
      return { text: "Sin captura nueva", color: "text.secondary" };
    }

    const rawValue = String(selectedInputValue || "").trim();
    if (!rawValue) {
      const currentDiff = toNumber(selectedItem?.diff_qty_base);
      if (Math.abs(currentDiff) <= 0.000001) {
        return { text: "Sin diferencia", color: "text.secondary" };
      }
      return {
        text: `Diferencia actual: ${formatDiffQty(selectedItem)}`,
        color: currentDiff > 0 ? "success.main" : "error.main",
      };
    }

    const qtyToAdd = toNumber(rawValue);
    const entryQtyInBase = toNumber(selectedEntryPresentation?.qty_in_base) || getQtyInBase(selectedItem);
    const projectedBase = toNumber(selectedItem?.counted_qty_base) + qtyToAdd * entryQtyInBase;
    const projectedDiffBase = projectedBase - toNumber(selectedItem?.system_qty_base);
    if (Math.abs(projectedDiffBase) <= 0.000001) {
      return { text: "Sin diferencia", color: "text.secondary" };
    }
    return {
      text: `${projectedDiffBase > 0 ? "Sobrante" : "Faltante"} estimado: ${formatStockCountReadableQty(
        Math.abs(projectedDiffBase),
        getStockCountPresentationsFromItem(selectedItem)
      )}`,
      color: projectedDiffBase > 0 ? "success.main" : "error.main",
    };
  }, [selectedEntryPresentation?.qty_in_base, selectedInputValue, selectedItem]);

  const reviewCorrectionMeta = useMemo(() => {
    if (!isReviewCorrectionMode || !selectedItem) return null;
    const rawValue = String(selectedInputValue || "").trim();
    if (!rawValue) return null;
    const qty = toNumber(rawValue);
    const qtyInBase = toNumber(selectedEntryPresentation?.qty_in_base) || getQtyInBase(selectedItem);
    const nextBase = qty * qtyInBase;
    const editingEntry = selectedEntries.find((entry) => String(entry?.id) === String(editingEntryId));
    const previousBase = editingEntry ? toNumber(editingEntry?.qty_base) : 0;
    const delta = nextBase - previousBase;
    const messages = [];
    if (delta > 0.000001) {
      messages.push(`Esta correccion incrementa el contado en ${formatStockCountReadableQty(delta, getStockCountPresentationsFromItem(selectedItem))}.`);
    } else if (delta < -0.000001) {
      messages.push(`Esta correccion reduce el contado en ${formatStockCountReadableQty(Math.abs(delta), getStockCountPresentationsFromItem(selectedItem))}.`);
    }
    if (
      editingEntry &&
      String(editingEntry?.product_presentation_id) !== String(selectedEntryPresentationId)
    ) {
      messages.push("Se recalculara el equivalente base del conteo.");
    }
    return messages.length ? messages.join(" ") : null;
  }, [
    editingEntryId,
    isReviewCorrectionMode,
    selectedEntries,
    selectedEntryPresentation?.qty_in_base,
    selectedEntryPresentationId,
    selectedInputValue,
    selectedItem,
  ]);

  const readOnlyAlert = useMemo(() => {
    if (!isReadOnly) return null;
    if (countStatus === "APPLIED") {
      return (
        <Alert severity="success">
          Este conteo fue aplicado el {formatDateTime(countAppliedAt) || "-"}.
          {" "}No se pueden realizar mas modificaciones.
        </Alert>
      );
    }
    if (countStatus === "CLOSED") {
      return <Alert severity="info">Este conteo esta cerrado.</Alert>;
    }
    if (countStatus === "PENDING_REVIEW") {
      return <Alert severity="info">La captura fue finalizada y esta pendiente de revision.</Alert>;
    }
    if (countStatus === "REVIEWING") {
      return <Alert severity="info">Este conteo esta en revision.</Alert>;
    }
    if (countStatus === "CANCELED") {
      return <Alert severity="info">Este conteo fue anulado.</Alert>;
    }
    return null;
  }, [countAppliedAt, countStatus, isReadOnly]);

  const clearSelection = useCallback(() => {
    setSelectedProductId(null);
    setSelectedItemId(null);
    setSearchQuery("");
    setHighlightedIndex(0);
    setEditingEntryId(null);
  }, [setSearchQuery]);

  const openGroup = useCallback(
    (group, preferredItemId = null) => {
      if (!group || isReadOnly) return;
      if (mustSelectGroup) {
        showSnack("Unete a un grupo antes de capturar conteos.", "warning");
        return;
      }
      const nextItem =
        group.items.find((item) => String(item.id) === String(preferredItemId)) ||
        group.items.find((item) => !item?.is_counted) ||
        group.items[0] ||
        null;
      setSelectedProductId(group.productId);
      setSelectedItemId(nextItem?.id || null);
      setSearchQuery("");
      setHighlightedIndex(0);
      setResetConfirmItemId(null);
      setEditingEntryId(null);
    },
    [isReadOnly, mustSelectGroup, setSearchQuery, showSnack]
  );

  useEffect(() => {
    if (!focusItemId || toArray(items).length === 0 || isReadOnly) return;
    const targetItem = toArray(items).find((item) => String(item?.id) === String(focusItemId));
    const targetProductId = targetItem?.product_presentation?.product?.id;
    if (!targetItem || !targetProductId) return;
    const targetGroup =
      productGroups.find((group) => String(group.productId) === String(targetProductId)) || null;
    if (!targetGroup) return;
    openGroup(targetGroup, targetItem.id);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("item_id");
    setSearchParams(nextParams, { replace: true });
  }, [focusItemId, isReadOnly, items, openGroup, productGroups, searchParams, setSearchParams]);

  const updateEntryDraft = (itemId, patch) => {
    setEntryDrafts((prev) => {
      const next = new Map(prev);
      next.set(itemId, {
        ...(next.get(itemId) || {}),
        ...patch,
      });
      return next;
    });
  };

  const clearEntryDraft = (itemId) => {
    setEntryDrafts((prev) => {
      const next = new Map(prev);
      next.delete(itemId);
      return next;
    });
  };

  const handleCrossIntegrityEvents = useCallback((result) => {
    const events = toArray(result?.cross_integrity_events);
    const autoCrossesRecalculated = Boolean(result?.auto_crosses_recalculated);
    if (events.length > 0) {
      setCrossIntegrityDialog({ open: true, events });
      showSnack("El conteo modificado afecto cruces existentes.", "warning");
      return true;
    }
    if (autoCrossesRecalculated) {
      showSnack("Los cruces automaticos fueron recalculados.", "warning");
      return true;
    }
    return false;
  }, [showSnack]);

  const startEditEntry = (entry) => {
    if (!selectedItem?.id || !entry?.id) return;
    setEditingEntryId(entry.id);
    updateEntryDraft(selectedItem.id, {
      product_presentation_id: entry.product_presentation_id,
      qty_presentation: entry.qty_presentation || "",
      note: entry.note || "",
      correction_reason: entry.correction_reason || "",
    });
    countInputRef.current?.focus();
  };

  const handleConfirmSelectedItem = async () => {
    if (!countId || !selectedItem?.id || isReadOnly) return;
    if (mustSelectGroup) {
      showSnack("Unete a un grupo antes de capturar conteos.", "warning");
      return;
    }

    const rawValue = String(selectedInputValue || "").trim();
    if (!rawValue) {
      showSnack("Ingresa una cantidad para agregar al conteo.", "warning");
      return;
    }

    const qtyToAdd = Number(rawValue);
    if (!Number.isFinite(qtyToAdd) || qtyToAdd <= 0) {
      showSnack("La cantidad a agregar debe ser un numero valido mayor a 0.", "warning");
      return;
    }
    if (!isPositiveIntegerInput(rawValue)) {
      showSnack(INTEGER_QTY_MESSAGE, "warning");
      return;
    }

    setSavingItem(true);
    try {
      let crossNoticeShown = false;
      if (typeof addEntry === "function") {
        const payload = {
          product_presentation_id: Number(selectedEntryPresentationId),
          qty_presentation: rawValue,
          note: selectedEntryDraft.note || "",
        };
        if (isReviewCorrectionMode) {
          payload.correction_reason = selectedEntryDraft.correction_reason || "";
        }
        if (groupsEnabled && activeGroup?.id && !isReviewCorrectionMode) {
          payload.stock_count_group_id = Number(activeGroup.id);
        }
        if (editingEntryId) {
          const result = await updateEntry(selectedItem.id, editingEntryId, payload);
          crossNoticeShown = handleCrossIntegrityEvents(result) || crossNoticeShown;
        } else {
          const result = await addEntry(selectedItem.id, payload);
          crossNoticeShown = handleCrossIntegrityEvents(result) || crossNoticeShown;
        }
      } else if (typeof addToItem === "function") {
        const result = await addToItem(selectedItem.id, rawValue);
        crossNoticeShown = handleCrossIntegrityEvents(result) || crossNoticeShown;
      }
      clearEntryDraft(selectedItem.id);
      setEditingEntryId(null);
      if (!crossNoticeShown) {
        showSnack(editingEntryId ? "Entrada actualizada correctamente." : "Conteo agregado correctamente.", "success");
      }
      countInputRef.current?.focus();
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail || "No se pudo guardar el item contado.";
      showSnack(detail, "error");
    } finally {
      setSavingItem(false);
    }
  };

  const handleVoidEntry = async () => {
    if (!selectedItem?.id || !voidDialog.entry?.id || typeof voidEntry !== "function") return;
    setSavingItem(true);
    try {
      const result = await voidEntry(selectedItem.id, voidDialog.entry.id, {
        void_reason: voidDialog.reason || "Correccion",
      });
      const crossNoticeShown = handleCrossIntegrityEvents(result);
      setVoidDialog({ open: false, entry: null, reason: "" });
      if (editingEntryId === voidDialog.entry.id) {
        setEditingEntryId(null);
        clearEntryDraft(selectedItem.id);
      }
      if (!crossNoticeShown) {
        showSnack("Entrada anulada correctamente.", "success");
      }
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail || "No se pudo anular la entrada.";
      showSnack(detail, "error");
    } finally {
      setSavingItem(false);
    }
  };

  const handleResetItem = useCallback(
    async (event, item) => {
      event.stopPropagation();
      if (!item?.id || typeof resetItem !== "function" || isReadOnly) return;

      if (resetConfirmItemId !== item.id) {
        setResetConfirmItemId(item.id);
        return;
      }

      setResettingItemId(item.id);
      try {
        const result = await resetItem(item.id);
        const crossNoticeShown = handleCrossIntegrityEvents(result);
        setResetConfirmItemId(null);
        clearEntryDraft(item.id);
        if (String(selectedItemId) === String(item.id)) {
          setSelectedItemId(item.id);
        }
        if (!crossNoticeShown) {
          showSnack("Conteo reiniciado para este item.", "success");
        }
      } catch (requestError) {
        const detail = requestError?.response?.data?.detail || "No se pudo reiniciar el conteo.";
        showSnack(detail, "error");
      } finally {
        setResettingItemId(null);
      }
    },
    [handleCrossIntegrityEvents, isReadOnly, resetConfirmItemId, resetItem, selectedItemId, showSnack]
  );

  const handleDeleteProduct = async () => {
    const group = deleteProductDialog.group;
    const itemId = toArray(group?.items)[0]?.id;
    if (!itemId || typeof deleteProduct !== "function" || isReadOnly) return;
    setDeletingProduct(true);
    try {
      await deleteProduct(itemId);
      setDeleteProductDialog({ open: false, group: null });
      clearSelection();
      showSnack("Producto eliminado del conteo selectivo.", "success");
    } catch (requestError) {
      showSnack(requestError?.response?.data?.detail || "No se pudo eliminar el producto del conteo.", "error");
    } finally {
      setDeletingProduct(false);
    }
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: "__rowNumber",
        headerName: "#",
        width: 64,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        valueGetter: (_, row) => row?.__rowNumber || "",
      },
      {
        field: "product",
        headerName: "Producto",
        flex: 1,
        minWidth: 300,
        sortable: false,
        renderCell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, justifyContent: "center" }}>
            <ProductFormatSummary product={row?.product_presentation?.product} nameSx={{ fontWeight: 600 }} />
            {hasPendingCashierSales(row) || row?.has_stock_movements_after_count_started ? (
              <Stack direction="row" spacing={0.4} useFlexGap flexWrap="wrap" sx={{ mt: 0.3 }}>
                {hasPendingCashierSales(row) ? (
                  <Tooltip
                    title={`Pendiente en Caja: ${formatPendingCashierQty(row)}. Estimado disponible: ${formatEstimatedAfterPendingCashierQty(row)}.`}
                    arrow
                  >
                    <Chip size="small" color="warning" variant="outlined" label="Pendiente en Caja" />
                  </Tooltip>
                ) : null}
                {row?.has_stock_movements_after_count_started ? (
                  <Chip size="small" color="warning" label="Tuvo movimiento" />
                ) : null}
              </Stack>
            ) : null}
          </Box>
        ),
      },
      {
        field: "format",
        headerName: "Formato",
        width: 120,
        sortable: false,
        valueGetter: (_, row) => getFormatName(row),
      },
      {
        field: "system_qty_base",
        headerName: "Sistema",
        width: 150,
        sortable: false,
        renderCell: ({ row }) => (
          <Typography sx={{ fontFamily: "monospace", fontSize: "0.82rem", fontWeight: 700 }}>
            {formatSystemQty(row)}
          </Typography>
        ),
      },
      {
        field: "counted_qty_presentation",
        headerName: "Contado",
        width: 160,
        sortable: false,
        renderCell: ({ row }) => (
          <Typography
            sx={{
              fontFamily: "monospace",
              fontSize: "0.82rem",
              fontWeight: row?.is_counted ? 700 : 400,
            }}
          >
            {row?.is_counted ? formatCountedQty(row) : "-"}
          </Typography>
        ),
      },
      {
        field: "diff_qty_base",
        headerName: "Diferencia",
        width: 150,
        sortable: false,
        renderCell: ({ row }) => {
          if (!row?.is_counted) {
            return <Chip size="small" variant="outlined" label="Pendiente" />;
          }
          const diff = toNumber(row?.diff_qty_base);
          const color = diff > 0 ? "success" : diff < 0 ? "error" : "default";
          return (
            <Chip
              size="small"
              color={color}
              variant={color === "default" ? "outlined" : "filled"}
              label={formatDiffQty(row)}
            />
          );
        },
      },
      {
        field: "crosses_qty",
        headerName: "Cruces",
        width: 160,
        sortable: false,
        renderCell: ({ row }) => {
          const incoming = toNumber(row?.crossed_in_qty_base);
          const outgoing = toNumber(row?.crossed_out_qty_base);
          const net = incoming - outgoing;
          if (Math.abs(net) <= 0.0001) return "-";
          return (
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "0.82rem",
                fontWeight: 700,
                color: net > 0 ? "success.main" : "info.main",
              }}
            >
              {formatStockCountReadableQty(net, getStockCountPresentationsFromItem(row), { showPlus: true })}
            </Typography>
          );
        },
      },
      {
        field: "cross_detail",
        headerName: "Cruce",
        minWidth: 220,
        flex: 0.6,
        sortable: false,
        renderCell: ({ row }) => {
          const rows = crossRowsByItem.get(String(row?.id)) || [];
          if (rows.length === 0) return "-";
          return <StockCountCrossDetailSummary rows={rows} item={row} formatQty={formatCrossRowQty} />;
        },
      },
      {
        field: "state",
        headerName: "Estado",
        width: 110,
        sortable: false,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            color={row?.is_counted ? "success" : "default"}
            variant={row?.is_counted ? "filled" : "outlined"}
            label={row?.is_counted ? "Contado" : "Pendiente"}
          />
        ),
      },
    ];

    if (isReadOnly) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        field: "reset",
        headerName: "",
        width: 70,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) =>
          row?.is_counted ? (
            <Tooltip
              title={
                resetConfirmItemId === row.id
                  ? "Confirma reinicio"
                  : "Reiniciar conteo de este item"
              }
            >
              <span>
                <IconButton
                  size="small"
                  color={resetConfirmItemId === row.id ? "error" : "default"}
                  onClick={(event) => handleResetItem(event, row)}
                  disabled={resettingItemId === row.id}
                >
                  <RestartAltOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : null,
      },
    ];
  }, [crossRowsByItem, formatCrossRowQty, handleResetItem, isReadOnly, resetConfirmItemId, resettingItemId]);

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.2}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <MainText title={title} subtitle={subtitle} icon={PlaylistAddCheckCircleOutlined} />
          {isReadOnly ? <Chip size="small" variant="outlined" label="Solo lectura" /> : null}
        </Stack>
        {isReviewCorrectionMode ? (
          <Button
            variant="contained"
            disabled={!countId || loading}
            onClick={() => navigate(reviewReturnPath || `/inventory/counts/${countId}/review`, { replace: true })}
          >
            Volver al resumen
          </Button>
        ) : !isReadOnly ? (
          <Button
            variant="contained"
            disabled={!countId || loading}
            onClick={async () => {
              if (!countId) return;
              const confirmed = window.confirm(
                "Al finalizar, ya no podras modificar este conteo. Pasara a revision/autorizacion."
              );
              if (!confirmed) return;
              try {
                let updatedCount = null;
                if (typeof submitCount === "function") {
                  updatedCount = await submitCount();
                }
                if (typeof onSubmitted === "function") {
                  await onSubmitted(updatedCount);
                } else {
                  navigate(`/inventory/counts/${updatedCount?.id || countId}/review`, { replace: true });
                }
              } catch (requestError) {
                const detail = requestError?.response?.data?.detail || "No se pudo finalizar la captura.";
                showSnack(detail, "error");
              }
            }}
          >
            Finalizar captura
          </Button>
        ) : null}
      </Stack>

      {headerAlert}
      {error ? <Alert severity="error">{error}</Alert> : null}
      {readOnlyAlert}
      {pendingCashierItems.length > 0 ? (
        <Alert severity="warning">
          Hay {pendingCashierItems.length} productos con ventas pendientes de cobro en Caja. La disponibilidad real puede ser menor que el stock del sistema.
        </Alert>
      ) : null}
      {settingsSnapshot?.selective_allow_sales_during_count && movedDuringCountItems.length > 0 ? (
        <Alert severity="warning">
          Hay {movedDuringCountItems.length} productos con movimientos de stock posteriores al inicio del conteo.
        </Alert>
      ) : null}

      {groupsEnabled ? (
        <Paper variant="outlined" sx={{ p: 1.2 }}>
          <Stack spacing={1}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Mi grupo actual
                </Typography>
                <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mt: 0.6 }}>
                  {activeGroup ? (
                    <>
                      <Chip color="primary" label={activeGroup.name || `Grupo ${activeGroup.id}`} />
                      {activeGroup.current_user_role ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={activeGroup.current_user_role === "DIGITATOR" ? "Digitador" : activeGroup.current_user_role === "COUNTER" ? "Contador" : "Apoyo"}
                        />
                      ) : null}
                    </>
                  ) : (
                    <Chip color="warning" variant="outlined" label="Sin grupo activo" />
                  )}
                </Stack>
                {currentUserGroup ? (
                  <Typography variant="caption" color="primary" display="block" sx={{ mt: 0.6, fontWeight: 700 }}>
                    Ya perteneces al {currentUserGroup.name}.
                  </Typography>
                ) : null}
              </Box>
              {!isReadOnly && !isReviewCorrectionMode ? (
                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                  <Button size="small" variant="outlined" onClick={() => setJoinGroupDialogOpen(true)} disabled={Boolean(currentUserGroup)}>
                    Unirme a grupo
                  </Button>
                </Stack>
              ) : null}
            </Stack>

            {isReviewCorrectionMode ? (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Correccion de revision: no requiere grupo de conteo.
              </Alert>
            ) : null}

            {!isReadOnly && !isReviewCorrectionMode && activeGroup ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={0.8} alignItems={{ sm: "center" }}>
                <TextField
                  size="small"
                  label="Agregar miembro"
                  value={memberNameDraft}
                  onChange={(event) => setMemberNameDraft(event.target.value)}
                  sx={{ maxWidth: { sm: 260 } }}
                />
                <TextField
                  select
                  size="small"
                  label="Rol operativo"
                  value={memberRoleDraft}
                  onChange={(event) => setMemberRoleDraft(event.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="DIGITATOR">Digitador</MenuItem>
                  <MenuItem value="COUNTER">Contador</MenuItem>
                </TextField>
                <Button size="small" variant="outlined" disabled={groupSubmitting || !memberNameDraft.trim()} onClick={handleAddMember}>
                  Agregar
                </Button>
              </Stack>
            ) : null}

            {mustSelectGroup ? (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                Debes unirte a un grupo antes de contar.
              </Alert>
            ) : null}
          </Stack>
        </Paper>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(5, minmax(0, 1fr))",
          },
          gap: 1.1,
        }}
      >
        <MetricCard label="Total" value={metrics.total} icon={<PlaylistAddCheckCircleOutlined fontSize="small" />} />
        <MetricCard
          label="Contados"
          value={metrics.counted}
          icon={<CheckCircleOutlineOutlined fontSize="small" />}
          color="success.main"
        />
        <MetricCard
          label="Pendientes"
          value={metrics.pending}
          icon={<HourglassEmptyOutlined fontSize="small" />}
          color="warning.main"
        />
        <MetricCard
          label="Con diff."
          value={metrics.withDiff}
          icon={<TrendingFlatOutlined fontSize="small" />}
          color="error.main"
        />
        <MetricCard
          label="Sin diff."
          value={metrics.withoutDiff}
          icon={<CheckCircleOutlineOutlined fontSize="small" />}
          color="info.main"
        />
      </Box>

      <Box sx={{ position: "relative" }}>
        <TextField
          inputRef={searchInputRef}
          autoFocus={!isReadOnly}
          size="small"
          placeholder={isReadOnly ? "Conteo cerrado - solo lectura" : "Buscar producto para contar..."}
          fullWidth
          disabled={isReadOnly}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          onKeyDown={(event) => {
            if (isReadOnly) return;
            if (event.key === "ArrowDown" && productGroups.length > 0) {
              event.preventDefault();
              setHighlightedIndex((prev) => Math.min(prev + 1, productGroups.length - 1));
            }
            if (event.key === "ArrowUp" && productGroups.length > 0) {
              event.preventDefault();
              setHighlightedIndex((prev) => Math.max(prev - 1, 0));
            }
            if (event.key === "Enter" && productGroups[highlightedIndex]) {
              event.preventDefault();
              openGroup(productGroups[highlightedIndex]);
            }
            if (event.key === "Escape") {
              event.preventDefault();
              clearSelection();
              searchInputRef.current?.focus();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {!isReadOnly && String(searchQuery || "").trim().length > 0 && productGroups.length > 0 ? (
          <Paper
            variant="outlined"
            sx={{
              position: "absolute",
              zIndex: 3,
              left: 0,
              right: 0,
              mt: 0.6,
              maxHeight: 280,
              overflowY: "auto",
            }}
          >
            <List disablePadding>
              {productGroups.map((group, index) => (
                <ListItemButton
                  key={group.productId}
                  selected={index === highlightedIndex}
                  onClick={() => openGroup(group)}
                  sx={{ alignItems: "flex-start", py: 1 }}
                >
                  <Box sx={{ minWidth: 0, width: "100%" }}>
                    <ProductFormatSummary product={group.product} name={group.description} code={group.code} />
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.6 }}>
                      {group.hasPendingCashierSales ? (
                        <Chip size="small" color="warning" variant="outlined" label="Pendiente en Caja" />
                      ) : null}
                      {group.hasMovementDuringCount ? (
                        <Chip size="small" color="warning" label="Tuvo movimiento" />
                      ) : null}
                      {group.items.map((item) => (
                        <Chip
                          key={`suggestion-format-${item.id}`}
                          size="small"
                          variant="outlined"
                          label={getFormatName(item)}
                        />
                      ))}
                    </Stack>
                  </Box>
                </ListItemButton>
              ))}
            </List>
          </Paper>
        ) : null}
      </Box>

      {!isReadOnly && selectedGroup && selectedItem ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1.3fr 1fr" },
            gap: 2,
            alignItems: "start",
          }}
        >
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack spacing={1.2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ md: "flex-start" }}
                gap={1.2}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <ProductFormatSummary
                    product={selectedGroup.product}
                    name={selectedGroup.description}
                    code={selectedGroup.code}
                    nameVariant="body1"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Sistema: {selectedSystemHumanized} | Base del item: {selectedSystemInFormat}
                  </Typography>
                </Box>

                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`Acumulado: ${formatCountedQty(selectedItem)}`}
                  sx={{ flexShrink: 0 }}
                />
              </Stack>

              <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
                {selectedGroup.items.map((item) => (
                  <Chip
                    key={`count-chip-${item.id}`}
                    size="small"
                    clickable
                    variant={String(item.id) === String(selectedItemId) ? "filled" : "outlined"}
                    color={String(item.id) === String(selectedItemId) ? "primary" : "default"}
                    label={getFormatName(item)}
                    onClick={() => setSelectedItemId(item.id)}
                  />
                ))}
                {typeof deleteProduct === "function" ? (
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteOutlineOutlined fontSize="small" />}
                    onClick={() => setDeleteProductDialog({ open: true, group: selectedGroup })}
                    disabled={savingItem || deletingProduct}
                  >
                    Eliminar producto
                  </Button>
                ) : null}
              </Stack>

              {groupsEnabled && activeGroup && !isReviewCorrectionMode ? (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Capturando para: {activeGroup.name}
                </Alert>
              ) : null}
              {hasPendingCashierSales(selectedItem) ? (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  Pendiente en Caja: {formatPendingCashierQty(selectedItem)}. Sistema {formatSystemQty(selectedItem)}; disponible estimado {formatEstimatedAfterPendingCashierQty(selectedItem)}.
                </Alert>
              ) : null}
              {selectedItem?.has_stock_movements_after_count_started ? (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  Este producto tuvo movimiento de stock durante el conteo.
                </Alert>
              ) : null}
              {reviewCorrectionMeta ? (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  {reviewCorrectionMeta}
                </Alert>
              ) : null}

              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.6 }}>
                  Formato
                </Typography>
                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                  {selectedAvailablePresentations.map((presentation) => (
                    <Chip
                      key={presentation.id}
                      clickable
                      label={`${presentation.format_name || "Presentacion"} (x${presentation.qty_in_base})`}
                      variant={
                        String(presentation.id) === String(selectedEntryPresentationId) ? "filled" : "outlined"
                      }
                      color={String(presentation.id) === String(selectedEntryPresentationId) ? "primary" : "default"}
                      onClick={() =>
                        updateEntryDraft(selectedItem.id, { product_presentation_id: presentation.id })
                      }
                    />
                  ))}
                </Stack>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <IconButton
                  aria-label="Restar"
                  size="small"
                  onClick={() => {
                    const rawValue = String(selectedInputValue || "").trim();
                    if (rawValue && hasDecimalSeparator(rawValue)) {
                      showSnack(INTEGER_QTY_MESSAGE, "warning");
                      return;
                    }
                    if (rawValue && !isPositiveIntegerInput(rawValue)) {
                      showSnack(INTEGER_QTY_MESSAGE, "warning");
                      return;
                    }
                    const currentValue = rawValue ? Number(rawValue) : 0;
                    const nextValue = Math.max(1, currentValue - 1);
                    updateEntryDraft(selectedItem.id, { qty_presentation: String(nextValue) });
                  }}
                >
                  <RemoveOutlined fontSize="small" />
                </IconButton>
                <TextField
                  inputRef={countInputRef}
                  autoFocus
                  type="text"
                  size="small"
                  label="Cantidad"
                  sx={{ width: 120 }}
                  value={
                    selectedInputValue === null ||
                      selectedInputValue === undefined ||
                      selectedInputValue === ""
                      ? ""
                      : String(parseInt(selectedInputValue, 10) || "")
                  }
                  onChange={(event) => {
                    const nextValue = event.target.value;

                    // Permitir vacío para edición
                    if (nextValue === "") {
                      updateEntryDraft(selectedItem.id, {
                        qty_presentation: "",
                      });
                      return;
                    }

                    // Solo enteros positivos
                    if (!/^\d+$/.test(nextValue)) {
                      showSnack(INTEGER_QTY_MESSAGE, "warning");
                      return;
                    }

                    updateEntryDraft(selectedItem.id, {
                      qty_presentation: nextValue,
                    });
                  }}
                  onKeyDown={(event) => {
                    if (
                      event.key === "." ||
                      event.key === "," ||
                      event.key === "-" ||
                      event.key === "+" ||
                      event.key === "e" ||
                      event.key === "E"
                    ) {
                      event.preventDefault();
                      return;
                    }

                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleConfirmSelectedItem();
                    }

                    if (event.key === "Escape") {
                      event.preventDefault();
                      clearSelection();
                      searchInputRef.current?.focus();
                    }
                  }}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    style: {
                      textAlign: "center",
                      fontWeight: 600,
                      fontSize: "1rem",
                    },
                  }}
                />
                <IconButton
                  aria-label="Sumar"
                  size="small"
                  onClick={() => {
                    const rawValue = String(selectedInputValue || "").trim();
                    if (rawValue && hasDecimalSeparator(rawValue)) {
                      showSnack(INTEGER_QTY_MESSAGE, "warning");
                      return;
                    }
                    if (rawValue && !isPositiveIntegerInput(rawValue)) {
                      showSnack(INTEGER_QTY_MESSAGE, "warning");
                      return;
                    }
                    const currentValue = rawValue ? Number(rawValue) : 0;
                    const nextValue = Math.max(1, currentValue + 1);
                    updateEntryDraft(selectedItem.id, { qty_presentation: String(nextValue) });
                  }}
                >
                  <AddOutlined fontSize="small" />
                </IconButton>
                <Typography
                  variant="caption"
                  sx={{ color: liveDiffMeta.color, fontWeight: 700, minWidth: { lg: 190 } }}
                >
                  {liveDiffMeta.text}
                </Typography>
                <Stack direction="row" spacing={0.8} sx={{ mt: 1.5 }}>
                  {editingEntryId ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setEditingEntryId(null);
                        clearEntryDraft(selectedItem.id);
                      }}
                      disabled={savingItem}
                    >
                      Cancelar
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleConfirmSelectedItem}
                    disabled={savingItem}
                  >
                    {isReviewCorrectionMode
                      ? editingEntryId
                        ? "Guardar correccion"
                        : "Agregar correccion"
                      : editingEntryId
                        ? "Guardar"
                        : "Agregar conteo"}
                  </Button>
                </Stack>
              </Stack>

              <Box>
                <Button
                  variant="text"
                  size="small"
                  startIcon={
                    <ExpandMoreOutlined
                      fontSize="small"
                      sx={{
                        transform: notesExpanded ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 160ms ease",
                      }}
                    />
                  }
                  onClick={() => setNotesExpanded((expanded) => !expanded)}
                >
                  Nota o motivo de correccion
                </Button>
                <Collapse in={notesExpanded}>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <TextField
                      size="small"
                      label="Nota"
                      value={selectedEntryDraft.note || ""}
                      onChange={(event) => updateEntryDraft(selectedItem.id, { note: event.target.value })}
                    />

                    {isReviewCorrectionMode ? (
                      <TextField
                        size="small"
                        label="Motivo de correccion"
                        value={selectedEntryDraft.correction_reason || ""}
                        onChange={(event) =>
                          updateEntryDraft(selectedItem.id, { correction_reason: event.target.value })
                        }
                      />
                    ) : null}
                  </Stack>
                </Collapse>
              </Box>

            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Pila de conteos
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedEntries.length} entradas
                </Typography>
              </Stack>
              {selectedEntries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Todavia no hay entradas para este producto.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
                  <Stack spacing={0.7}>
                    {selectedEntries.map((entry, entryIndex) => {
                      const isVoided = Boolean(entry?.is_voided);
                      return (
                        <Box
                          key={entry.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            py: 0.7,
                            px: 1,
                            borderLeft: (theme) => {
                              const borderColor = isVoided
                                ? theme.palette.divider
                                : entry?.entry_source === "REVIEW_CORRECTION"
                                  ? theme.palette.warning.main
                                  : theme.palette.primary.main;
                              return `3px solid ${borderColor}`;
                            },
                            bgcolor: isVoided ? "transparent" : "action.hover",
                            borderRadius: "0 8px 8px 0",
                          }}
                        >
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: "50%",
                              bgcolor: "background.paper",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              {entryIndex + 1}
                            </Typography>
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                fontFamily: "monospace",
                                textDecoration: isVoided ? "line-through" : "none",
                                color: isVoided ? "text.disabled" : "text.primary",
                              }}
                            >
                              {formatDisplayQty(entry?.qty_presentation, 3)}{" "}
                              {entry?.product_presentation?.format_name || "Presentacion"}
                              {" = "}
                              {formatStockCountReadableQty(entry?.qty_base, getStockCountPresentationsFromItem(selectedItem))}
                            </Typography>
                            <Typography
                              variant="caption"
                              color={isVoided ? "text.disabled" : "text.secondary"}
                              display="block"
                            >
                              {[entry?.effective_group_name || entry?.group_name_snapshot || entry?.group_name, entry?.counter_name].filter(Boolean).join(" | ") || "Sin grupo"}
                              {" | "}
                              {formatDateTime(entry?.created_at) || "-"}
                            </Typography>
                            {entry?.entry_source === "REVIEW_CORRECTION" && entry?.correction_reason ? (
                              <Typography variant="caption" sx={{ color: "warning.main" }} display="block">
                                {entry.correction_reason}
                              </Typography>
                            ) : null}
                            {entry?.note ? (
                              <Typography
                                variant="caption"
                                color={isVoided ? "text.disabled" : "text.secondary"}
                                display="block"
                              >
                                {entry.note}
                              </Typography>
                            ) : null}
                            {isVoided ? (
                              <Typography variant="caption" color="text.disabled" display="block">
                                Anulada: {entry?.void_reason || "Sin motivo"}
                              </Typography>
                            ) : null}
                          </Box>
                          {!isVoided ? (
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="Editar entrada">
                                <IconButton
                                  size="small"
                                  color="default"
                                  onClick={() => startEditEntry(entry)}
                                  disabled={savingItem}
                                >
                                  <EditOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Anular entrada">
                                <IconButton
                                  size="small"
                                  color="default"
                                  onClick={() => setVoidDialog({ open: true, entry, reason: "" })}
                                  disabled={savingItem}
                                >
                                  <BlockOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Paper>
        </Box>
      ) : null}

      <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
        {[
          { key: "ALL", label: "Todos" },
          { key: "COUNTED", label: "Contados" },
          { key: "PENDING", label: "Pendientes" },
        ].map((option) => (
          <Chip
            key={option.key}
            clickable
            label={option.label}
            color={filterMode === option.key ? "primary" : "default"}
            variant={filterMode === option.key ? "filled" : "outlined"}
            onClick={() => setFilterMode(option.key)}
          />
        ))}
      </Stack>

      <LinearProgress variant="determinate" value={progressValue} sx={{ height: 8, borderRadius: 999 }} />

      <Paper variant="outlined" sx={{ height: "calc(100vh - 330px)", minHeight: 440 }}>
        <DataGrid
          rows={numberedFilteredItems}
          columns={columns}
          getRowId={(row) => row.id}
          getRowHeight={() => "auto"}
          loading={loading}
          onRowClick={
            isReadOnly
              ? undefined
              : (params) => {
                const productId = params.row?.product_presentation?.product?.id;
                const group = productGroups.find((entry) => String(entry.productId) === String(productId));
                openGroup(group, params.row?.id);
              }
          }
          disableRowSelectionOnClick
          pageSizeOptions={[20, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 20, page: 0 } },
          }}
          getRowClassName={(params) =>
            params.row?.is_counted ? "stock-count-row-counted" : "stock-count-row-pending"
          }
          sx={{
            "& .MuiDataGrid-cell": {
              py: 0.8,
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#2C2C2A",
              color: "#ffffff",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover": {
              backgroundColor: "#2C2C2A !important",
            },
            "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator": {
              color: "#ffffff !important",
            },
            "& .stock-count-row-pending": {
              color: "text.secondary",
            },
            "& .stock-count-row-counted": {
              bgcolor: "background.paper",
            },
          }}
        />
      </Paper>

      <Dialog
        open={joinGroupDialogOpen}
        onClose={() => {
          if (groupSubmitting) return;
          setJoinGroupDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Unirme a grupo</DialogTitle>
        <DialogContent dividers>
          {!currentUserGroup ? (
            <TextField
              select
              fullWidth
              size="small"
              label="Mi rol operativo"
              value={joinGroupRole}
              onChange={(event) => setJoinGroupRole(event.target.value)}
              sx={{ mb: 1.2 }}
            >
              <MenuItem value="DIGITATOR">Digitador</MenuItem>
              <MenuItem value="COUNTER">Contador</MenuItem>
              <MenuItem value="SUPPORT">Apoyo</MenuItem>
            </TextField>
          ) : null}
          {stockCountGroups.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Todavia no hay grupos creados para este conteo.
            </Typography>
          ) : (
            <Stack spacing={0.8}>
              {stockCountGroups.map((group) => (
                <Paper key={group.id} variant="outlined" sx={{ p: 1 }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {group.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Digitadores: {toArray(group.digitators).join(", ") || "Sin asignar"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Contadores: {toArray(group.counters).join(", ") || "Sin asignar"}
                      </Typography>
                      {group?.current_user_is_member ? (
                        <Typography variant="caption" color="primary" display="block" sx={{ fontWeight: 700 }}>
                          Ya perteneces a este grupo
                        </Typography>
                      ) : null}
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={groupSubmitting || Boolean(currentUserGroup)}
                      onClick={() => handleJoinGroup(group)}
                    >
                      Unirme
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJoinGroupDialogOpen(false)} disabled={groupSubmitting}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={voidDialog.open}
        onClose={() => {
          if (savingItem) return;
          setVoidDialog({ open: false, entry: null, reason: "" });
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Anular entrada</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.2}>
            <Typography variant="body2" color="text.secondary">
              La entrada no se borrara; quedara marcada como anulada y el acumulado se recalculara.
            </Typography>
            <TextField
              label="Motivo"
              fullWidth
              multiline
              minRows={2}
              value={voidDialog.reason}
              onChange={(event) =>
                setVoidDialog((prev) => ({ ...prev, reason: event.target.value }))
              }
              placeholder="Correccion"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setVoidDialog({ open: false, entry: null, reason: "" })}
            disabled={savingItem}
          >
            Cancelar
          </Button>
          <Button color="error" variant="contained" onClick={handleVoidEntry} disabled={savingItem}>
            Anular
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteProductDialog.open}
        onClose={() => {
          if (deletingProduct) return;
          setDeleteProductDialog({ open: false, group: null });
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Eliminar producto del conteo</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.2}>
            <Typography variant="body2">
              Estas seguro de eliminar este producto del conteo selectivo?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Esta accion eliminara sus cantidades capturadas, sus entradas de conteo y cruces asociados aun no aplicados.
            </Typography>
            {productGroupHasRegisteredData(deleteProductDialog.group) ? (
              <Alert severity="warning">
                Este producto ya tiene informacion registrada. Al eliminarlo tambien se eliminaran los cruces pendientes relacionados.
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteProductDialog({ open: false, group: null })} disabled={deletingProduct}>
            Cancelar
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteProduct} disabled={deletingProduct}>
            Eliminar producto
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={crossIntegrityDialog.open}
        onClose={() => setCrossIntegrityDialog({ open: false, events: [] })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>El conteo modificado afecto cruces existentes</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            {toArray(crossIntegrityDialog.events).map((event) => {
              const sourceItem = toArray(items).find((item) => String(item?.id) === String(event?.source_item_id));
              return (
                <Paper key={`${event.cross_id}-${event.after_qty_base}`} variant="outlined" sx={{ p: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    Cruce {event.source_product} -> {event.target_product}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Antes: {formatStockCountReadableQty(event.before_qty_base, getStockCountPresentationsFromItem(sourceItem))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Ahora: {formatStockCountReadableQty(event.after_qty_base, getStockCountPresentationsFromItem(sourceItem))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Motivo: {event.integrity_note || "-"}
                  </Typography>
                </Paper>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCrossIntegrityDialog({ open: false, events: [] })}>
            Cerrar
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setCrossIntegrityDialog({ open: false, events: [] });
              navigate(`/inventory/counts/${countId}/review`);
            }}
          >
            Ver cruces afectados
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}

export default StockCountPage;
